import SubjectMediaFields from '@/components/SubjectMediaFields';
import {hasLocalizedText} from '@/components/BilingualField';
import MainImagePicker from "@/components/MainImagePicker";
import GalleryEditor from '@/components/GalleryEditor';
import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, ArrowRight, ArrowLeft, Circle, PartyPopper, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";
import { useAuth } from "@/auth/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import SubjectFields from "@/components/SubjectFields";
import LivestockUnits from "@/components/LivestockUnits";
import ProjectFields from "@/components/ProjectFields";
import { DETAILS, SUBJECTS, REGISTRATION_PATHS, subjectLabel, hasPlantManagement, hasPlantAreas, hasCropCultivation } from "@/lib/subjects";
import { formatApiError } from "@/lib/api";
import CultivarPicker from "@/components/CultivarPicker";

import SubjectLocationFields from "@/components/SubjectLocationFields";
import ReferenceCatalog from "@/components/ReferenceCatalog";
import PlantHeritage from "@/components/PlantHeritage";
import PlanComparison from "@/components/PlanComparison";
import CropAreas, { serializeAreas } from "@/components/CropAreas";

export default function Onboarding() {
  const { t, lang, tv } = useLang();
  const { user, refreshUser } = useAuth();
  const [params] = useSearchParams();
  const initialType = REGISTRATION_PATHS.find(r => r[0] === (params.get('registration_path') || user?.registration_path))?.[3][0] || 'farm';
  const [step, setStep] = useState(1);
  const [crops, setCrops] = useState([]);
  const [species, setSpecies] = useState([]);
  const [botanicalSelections, setBotanicalSelections] = useState({});
  const [customCultivars, setCustomCultivars] = useState({});
  // Each category may contain multiple species, each with multiple cultivars.
  const [livestockUnits, setLivestockUnits] = useState([]);
  const [initialProject, setInitialProject] = useState(null);
  const [chosenPlan, setChosenPlan] = useState("free");
  const [registrationPlan, setRegistrationPlan] = useState(null);
  const [submitError, setSubmitError] = useState("");
  const [created, setCreated] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [mediaUploading,setMediaUploading] = useState(false);
  const [submitSeconds, setSubmitSeconds] = useState(0);
  useEffect(() => {
    if (!submitting) return;
    const started = Date.now();
    setSubmitSeconds(0);
    const timer = window.setInterval(() => setSubmitSeconds(Math.floor((Date.now() - started) / 1000)), 1000);
    return () => window.clearInterval(timer);
  }, [submitting]);
  const [form, setForm] = useState({
    name: "", country: "", region: "", municipality: "", locality: "",
    original_language: lang, description_translations: {}, subject_translations: {},
    subject_type: initialType, activities: SUBJECTS.find(r => r[0] === initialType)[3], subject_details: {},
    reference_crop_ids: [], reference_botanical_selections: [], plant_heritage: [],
    social_links: {}, video: null,
    fields: [], crops: [], description: "", image: "", website: "", shop_url: "",
    practices: { water_methods: '', water_source: '', soil: '', biodiversity: '', agricultural: '' },
  });

  useEffect(() => {
    api.get("/crops").then((r) => setCrops(r.data)).catch(() => {});
    api.get("/botanical/species").then((r) => setSpecies(r.data)).catch(() => {});
  }, []);

  const userId = user?.id;
  useEffect(() => {
    if (!userId) { setRegistrationPlan(null); return; }
    const controller = new AbortController(); setRegistrationPlan(null);
    api.get('/my/registration-plan', { signal: controller.signal }).then(r => setRegistrationPlan(r.data)).catch(() => {});
    return () => controller.abort();
  }, [userId]);

  const canCreateProject = form.subject_type === 'public_body' || registrationPlan?.effective_plan === 'founder_pro';

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleCrop = (name) => {
    const category = crops.find((c) => c.name === name);
    if (form.crops.includes(name) && category) {
      setBotanicalSelections((prev) => {
        const next = { ...prev };
        delete next[category.id];
        return next;
      });
    }
    setForm((f) => ({
      ...f,
      crops: f.crops.includes(name) ? f.crops.filter((c) => c !== name) : [...f.crops, name],
    }));
  };

  const canNext = () => {
    if (step === 1) return form.name.trim() && form.country;
    if (step === 2) return (!hasCropCultivation(form.activities) || (form.crops.length > 0 || form.reference_crop_ids.length > 0)) &&
      (!form.activities.includes("livestock") || (livestockUnits.length > 0 && livestockUnits.every(u =>
        u.species_id && (u.species_id !== "other" || u.custom_species.trim()) &&
        (u.breed_status !== "specified" || u.breed_ids.length > 0) &&
        (u.breed_status !== "other" || u.custom_breed.trim()) &&
        (u.head_count == null || (Number.isInteger(u.head_count) && u.head_count >= 0)))));
    return true;
  };

  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    setSubmitError("");
    try {
      const { data } = await api.post("/farms", {
        subject_type: form.subject_type,
        activities: form.activities,
        subject_details: form.subject_details,
        mushroom_units: form.mushroom_units,
        portfolio_files: form.portfolio_files || [],
        linked_farm_ids: form.subject_type === "professional" ? form.linked_farm_ids || [] : [],
        plant_heritage: form.plant_heritage,
        fields: hasPlantAreas(form.activities) ? serializeAreas(form.fields) : [],
        practices: hasPlantManagement(form.activities) ? Object.fromEntries(Object.entries(form.practices).map(([k, v]) => [k, k === 'water_source' ? v.trim() || null : v.split(',').map(s => s.trim()).filter(Boolean)])) : {},
        livestock_units: form.activities.includes("livestock") ? livestockUnits : [],
        opportunities: canCreateProject && hasLocalizedText(initialProject?.title) ? [{ ...initialProject, active: true }] : [],
        name: form.name,
        reference_crop_ids: form.reference_crop_ids,
        reference_botanical_selections: form.reference_botanical_selections,
        country: form.country,
        lat: form.lat ?? null, lng: form.lng ?? null,
        region: form.region || null,
        locality: form.locality || null,
        municipality: form.municipality || null,
        crops: hasCropCultivation(form.activities) ? form.crops : [],
        botanical_selections: Object.values(hasCropCultivation(form.activities) ? botanicalSelections : {}).flatMap((bySpecies) =>
          Object.entries(bySpecies).flatMap(([species_id, cultivarIds]) => {
            const custom = (customCultivars[species_id] || "").trim();
            return [
              ...cultivarIds.map((cultivar_id) => ({ species_id, cultivar_id })),
              ...(custom ? [{ species_id, custom_cultivar: custom }] : []),
              ...(!cultivarIds.length && !custom ? [{ species_id }] : []),
            ];
          })
        ),
        original_language: form.original_language,
        description_translations: form.description_translations,
        subject_translations: form.subject_translations,
        cover_image: form.image || null,
        profile_image: form.profile_image || null,
        website: form.website || null,
        social_links: form.social_links, video: form.video,
        shop_url: form.shop_url || null,
      });
      setCreated(data);
      if (user) {
        // Update the session role and farm IDs after ownership is created.
        refreshUser().catch(() => {});
      }
    } catch (e) {
      const message = formatApiError(e.response?.data?.detail) || (lang === "it" ? "Impossibile salvare. Riprova." : "Unable to save. Please retry.");
      setSubmitError(message); toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (created) {
    const pct = created.completeness?.percent ?? 0;
    const enrich = (hasCropCultivation(form.activities) ? [
      { k: "addVarieties", done: created.completeness?.checks?.varieties },
      { k: "addField", done: created.completeness?.checks?.field_information },
      { k: "tellHow", done: created.completeness?.checks?.farming_practices },
      { k: "addCerts", done: created.completeness?.additional?.certifications },
      { k: "addProducts", done: false },
      { k: "addOpps", done: created.completeness?.additional?.opportunities },
    ] : [{ k: "addOpps", done: (created.opportunities || []).length > 0 }]);
    return (
      <div className="grain-bg min-h-[calc(100vh-72px)] flex items-center py-16" data-testid="onboarding-complete">
        <div className="max-w-2xl mx-auto px-4 w-full text-center fade-up">
          <span className="w-16 h-16 rounded-full bg-[#EAF0E6] inline-flex items-center justify-center mb-6">
            <PartyPopper size={30} className="text-[#557448]" />
          </span>
          <h1 className="font-serif text-4xl sm:text-5xl font-bold text-[#2A2521]">{t("onboarding.doneTitle")}</h1>
          <p className="font-sans text-[#6E6258] mt-3">{t("onboarding.doneSub")}</p>

          <div className="bg-[#FFFDF8] rounded-md border border-[#E2DACB] p-6 mt-8 text-left">
            <div className="flex items-center justify-between mb-4">
              <span className="font-sans text-sm text-[#4A443D]">
                {t("onboarding.profileComplete")} <strong className="text-[#557448]">{pct}%</strong> {t("onboarding.complete")}
              </span>
            </div>
            <div className="h-2.5 rounded-full bg-[#EDE7D7] overflow-hidden">
              <div className="h-full bg-[#557448] rounded-full" style={{ width: `${pct}%`, transition: "width .8s ease" }} />
            </div>
            <p className="font-sans text-xs font-semibold uppercase tracking-wider text-[#C46A3A] mt-6 mb-3">{t("onboarding.enrich")}</p>
            <p className="text-sm text-[#6E6258] mb-3">{lang === 'it' ? 'Suggerimenti facoltativi per arricchire la scheda. Certificazioni e opportunità non incidono sulla percentuale di completezza.' : 'Optional suggestions to enrich your profile. Certifications and opportunities do not affect the completeness percentage.'}</p>
            <ul className="space-y-2">
              {enrich.map((e) => (
                <li key={e.k} className="flex items-center gap-2 text-sm font-sans">
                  {e.done ? <Check size={15} className="text-[#557448]" /> : <Circle size={13} className="text-[#C8BBA3]" />}
                  <span className={e.done ? "text-[#4A443D]" : "text-[#6E6258]"}>{t(`onboarding.${e.k}`)}{e.k === 'addOpps' && !e.done && <span className="block text-xs mt-1">{created.subject_type === 'public_body' ? (lang === 'it' ? 'Un progetto incluso con Free; per altre opportunità occorre Pro.' : 'One project is included with Free; other opportunities require Pro.') : (lang === 'it' ? 'Richiede Pro; incluso per i soggetti coperti da un piano a vita.' : 'Requires Pro; included for subjects covered by a lifetime plan.')}</span>}</span>
                </li>
              ))}
            </ul>
          </div>

          {user && created.owner_id === user.id && <div className="mt-6"><GalleryEditor originalLanguage={form.original_language} farmId={created.id} /></div>}
          {chosenPlan === 'pro' && !registrationPlan?.lifetime && created.subject_type !== 'private' && <div className="mt-6">
            <p className="text-sm">{lang === 'it' ? 'La scheda è stata creata con Free. Puoi ora attivare Pro a 50 €/anno nella sezione Piano e incassi.' : 'Your profile was created with Free. You can now activate Pro at €50/year in Plan and payments.'}</p>
            <Link data-testid="activate-pro" className="block underline mt-3" to={`/dashboard/farms/${created.id}/billing`}>{lang === 'it' ? 'Vai all’attivazione Pro' : 'Activate Pro'}</Link>
          </div>}
          {registrationPlan?.lifetime && <p data-testid="founder-registration" className="mt-6">{lang === 'it' ? 'Il tuo account verificato copre questa scheda con Founder Pro gratuito a vita.' : 'Your verified account covers this profile with complimentary lifetime Founder Pro.'}</p>}
          {user && created.owner_id === user.id && (
            <Button asChild data-testid="go-to-dashboard" className="mt-8 mr-3 h-12 rounded-full bg-[#557448] hover:bg-[#46633b] text-[#F8F5EA] px-8 font-sans font-medium">
              <Link to="/dashboard">{t("onboarding.goDashboard")}</Link>
            </Button>
          )}
          <Button asChild data-testid="view-my-farm" className="mt-8 h-12 rounded-full border border-[#557448] bg-transparent hover:bg-[#EAF0E6] text-[#557448] px-8 font-sans font-medium">
            <Link to={`/farm/${created.id}`}>{t("onboarding.viewMyFarm")}</Link>
          </Button>
        </div>
      </div>
    );
  }

  const activityTitle = hasCropCultivation(form.activities) ? (lang === "it" ? "Colture e attività" : "Crops and activities")
    : form.activities.includes("livestock") ? (lang === "it" ? "Animali e allevamento" : "Animals and livestock")
    : `${lang === "it" ? "Attività e dettagli" : "Activities and details"} — ${subjectLabel(form.subject_type, lang)}`;
  const stepTitles = [lang === "it" ? "Identità e località" : "Identity and location", activityTitle, t("onboarding.step3"), lang === "it" ? "Contatti e progetto" : "Contacts and project"];

  return (
    <div className="grain-bg min-h-[calc(100vh-72px)] py-12 sm:py-16" data-testid="onboarding-page">
      <div className="max-w-xl mx-auto px-4">
        <div className="text-center mb-8">
          <h1 className="font-serif text-4xl font-bold text-[#2A2521]">{lang === "it" ? "Registra un soggetto" : "Register a subject"}</h1>
          <p className="font-sans text-[#6E6258] mt-2">{lang === "it" ? "Un profilo, più attività e opportunità." : "One profile, multiple activities and opportunities."}</p>
        </div>

        {/* progress */}
        <div className="flex items-center gap-2 mb-8">
          {[1, 2, 3, 4].map((s) => (
            <div key={s} className={`h-1.5 flex-1 rounded-full ${s <= step ? "bg-[#557448]" : "bg-[#E2DACB]"}`} />
          ))}
        </div>

        <div className="bg-[#FFFDF8] rounded-md border border-[#E2DACB] p-6 sm:p-8">
          <p className="font-sans text-xs font-semibold uppercase tracking-[0.18em] text-[#C46A3A]">
            {t("onboarding.step")} {step} {t("onboarding.of")} 4
          </p>
          <h2 className="font-serif text-2xl font-semibold text-[#2A2521] mt-1 mb-6">{stepTitles[step - 1]}</h2>

          {step === 1 && (
            <div className="space-y-5">
              <SubjectFields value={form} onChange={setForm} mode="identity" />
              <Field label={lang === "it" ? "Nome pubblico" : "Public name"}>
                <Input data-testid="ob-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder={lang === "it" ? "Nome del soggetto…" : "Subject name…"} className="bg-white border-[#E2DACB]" />
              </Field>
              <SubjectLocationFields value={form} onChange={setForm} prefix="ob" />
            </div>
          )}

          {step === 2 && <div className="space-y-6 mb-6">
            <SubjectFields value={form} onChange={setForm} mode="details" />
            {form.activities.includes('mushrooms') && <section className="border rounded-md p-4 space-y-3"><h3 className="font-serif text-xl">{lang==='it'?'Fungaia e funghi coltivati':'Mushroom farm and cultivated fungi'}</h3><SubjectFields value={form} onChange={setForm} mode="fungi"/></section>}
            <ReferenceCatalog value={form} onChange={setForm} />
            {hasPlantAreas(form.activities) && <CropAreas botanicalSelections={form.reference_botanical_selections} botanicalOnly={!hasCropCultivation(form.activities)} originalLanguage={form.original_language} mode="details" generalPractices={form.practices} value={form.fields.filter(row=>hasCropCultivation(form.activities) || row.reference_species_id)} onChange={rows => set("fields", [...form.fields.filter(row=>!hasCropCultivation(form.activities) && !row.reference_species_id),...rows])} referenceIds={hasCropCultivation(form.activities) ? form.reference_crop_ids : []} legacyCrops={hasCropCultivation(form.activities) ? form.crops : []} />}
              <details open={form.plant_heritage.length>0}><summary className="cursor-pointer font-serif text-xl">{lang==='it'?'Censimenti e interventi sul patrimonio vegetale (facoltativi)':'Plant heritage surveys and interventions (optional)'}</summary><PlantHeritage originalLanguage={form.original_language} value={form.plant_heritage} onChange={v => set("plant_heritage", v)} subjectType={form.subject_type} /></details>
            {form.activities.includes("livestock") && <LivestockUnits originalLanguage={form.original_language} value={livestockUnits} onChange={setLivestockUnits} />}
            {!hasCropCultivation(form.activities) && !form.activities.includes("livestock") && !DETAILS[form.subject_type]?.length && <p className="text-sm">{lang === "it" ? "Descrivi la tua attività al prossimo passo." : "Describe your activity in the next step."}</p>}
          </div>}
          {step === 3 && (
            <div className="space-y-5">
              {hasPlantAreas(form.activities) && <>
                <p className="text-sm">{lang === 'it' ? 'Le pratiche già inserite sono conservate e mostrate per ogni coltura o specie e area. Modifica soltanto ciò che serve.' : 'Previously entered practices are preserved for each crop or species and area. Only change what is needed.'}</p>
                <CropAreas botanicalSelections={form.reference_botanical_selections} botanicalOnly={!hasCropCultivation(form.activities)} originalLanguage={form.original_language} mode="practices" generalPractices={form.practices} value={form.fields.filter(row=>hasCropCultivation(form.activities) || row.reference_species_id)} onChange={rows => set('fields', [...form.fields.filter(row=>!hasCropCultivation(form.activities) && !row.reference_species_id),...rows])} referenceIds={hasCropCultivation(form.activities) ? form.reference_crop_ids : []} legacyCrops={hasCropCultivation(form.activities) ? form.crops : []} />

              </>}
              <Field label={lang === 'it' ? 'Lingua originale della scheda' : 'Original profile language'}><select value={form.original_language} onChange={e => set('original_language', e.target.value)} className="border rounded p-2"><option value="it">Italiano</option><option value="en">English</option></select></Field>
              {['it', 'en'].map(code => <Field key={code} label={`${lang === 'it' ? 'Descrizione' : 'Description'} — ${code === 'it' ? 'Italiano' : 'English'}${code !== form.original_language ? (lang === 'it' ? ' (facoltativa)' : ' (optional)') : ''}`}>
                <Textarea value={form.description_translations[code] || ''} onChange={e => set('description_translations', { ...form.description_translations, [code]: e.target.value })} maxLength={10000} rows={4} />
              </Field>)}
              <Field label={lang === "it" ? "Foto o logo del profilo (facoltativa)" : "Profile photo or logo (optional)"}>
                <MainImagePicker value={form.profile_image} onChange={value => set("profile_image", value)} lang={lang} variant="profile" />
              </Field>
              <Field label={lang === "it" ? "Immagine di copertina (facoltativa)" : "Cover image (optional)"}>
                <MainImagePicker value={form.image} onChange={value => set("image", value)} lang={lang} />
              </Field>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-5">
              <Field label={t("onboarding.websiteOpt")}>
                <Input data-testid="ob-website" value={form.website} onChange={(e) => set("website", e.target.value)} placeholder="https://…" className="bg-white border-[#E2DACB]" />
              </Field>
              <SubjectMediaFields socialLinks={form.social_links} video={form.video} onSocialChange={value=>set("social_links",value)} onVideoChange={value=>set("video",value)} onBusyChange={setMediaUploading} />
              <div className="border rounded-md p-4 mb-5"><PlanComparison />
                {registrationPlan?.lifetime ? <p data-testid="founder-plan-choice" className="mt-3 text-sm">{lang === 'it' ? 'Founder Pro gratuito a vita: nessun abbonamento da acquistare.' : 'Complimentary lifetime Founder Pro: no subscription purchase needed.'}</p> : form.subject_type !== 'private' && <label className="block text-sm mt-3">{lang === 'it' ? 'Piano desiderato' : 'Preferred plan'}
                  <select data-testid="registration-plan" className="block w-full border rounded-md p-2 mt-1" value={chosenPlan} onChange={e => setChosenPlan(e.target.value)}><option value="free">Free</option><option value="pro">Pro — {lang === 'it' ? '50 €/anno' : '€50/year'}</option></select>
                  <span className="block text-xs mt-2">{lang === 'it' ? 'La scelta Pro non produce addebiti: dopo la registrazione completi l’attivazione. Fino ad allora si applica Free.' : 'Choosing Pro makes no charge: complete activation after registration. Until then Free applies.'}</span>
                </label>}
              </div>
              <div className="border-t pt-4">
                <p className="text-sm mb-3">{lang === 'it' ? 'Solo Comuni ed enti pubblici possono creare un progetto con Free. Per gli altri soggetti i progetti richiedono Pro.' : 'Only municipalities and public bodies can create one project with Free. Other subjects need Pro for projects.'}</p>
                {canCreateProject ? initialProject ? <><ProjectFields originalLanguage={form.original_language} value={initialProject} onChange={setInitialProject} /><button type="button" className="text-sm underline mt-3" onClick={() => setInitialProject(null)}>{lang === "it" ? "Rimanda il progetto" : "Add a project later"}</button></> : <button type="button" className="text-sm text-[#557448] underline" onClick={() => setInitialProject({ id: crypto.randomUUID(), type: "PROJECT", title: "", description: "", project_details: {} })}>{lang === "it" ? "+ Aggiungi un progetto (facoltativo)" : "+ Add a project (optional)"}</button> : <p className="text-sm" data-testid="registration-project-pro">{lang === "it" ? "Dopo aver creato la scheda, attiva Pro nella sezione Piano e incassi per aggiungere progetti." : "After creating the profile, activate Pro in Plan and payments to add projects."}</p>}
              </div>
            </div>
          )}

          {submitting && <div className="mt-6 rounded-lg bg-[#F0F3EA] p-4 text-[#46633b]" aria-busy="true">
            <p role="status" className="flex items-center gap-2">
              <Loader2 size={20} className="animate-spin motion-reduce:animate-none shrink-0" aria-hidden="true" />
              {lang === "it" ? "Creazione della scheda in corso…" : "Creating your profile…"}
            </p>
            <p className="mt-2 text-sm" aria-live="off">{lang === "it" ? "Tempo trascorso" : "Elapsed time"}: {submitSeconds} s</p>
            <p className="mt-1 text-sm">{lang === "it" ? "Attendi senza chiudere o ricaricare la pagina. L’operazione può richiedere qualche istante." : "Please keep this page open without refreshing. This may take a little time."}</p>
          </div>}
          {submitError && <p role="alert" className="text-sm text-[#B3261E] mt-4">{submitError}</p>}
          <div className="flex items-center justify-between mt-8">
            <button
              data-testid="ob-back"
              disabled={submitting}
              onClick={() => setStep((s) => Math.max(1, s - 1))}
              className={`inline-flex items-center gap-1.5 font-sans text-sm text-[#6E6258] ${step === 1 ? "invisible" : ""}`}
            >
              <ArrowLeft size={16} /> {t("onboarding.back")}
            </button>
            {step < 4 ? (
              <Button
                data-testid="ob-next"
                disabled={!canNext()}
                onClick={() => setStep((s) => s + 1)}
                className="rounded-full bg-[#557448] hover:bg-[#46633b] text-[#F8F5EA] px-6 gap-1.5 disabled:opacity-40"
              >
                {t("onboarding.next")} <ArrowRight size={16} />
              </Button>
            ) : (
              <Button
                data-testid="ob-finish"
                disabled={submitting || mediaUploading || (canCreateProject && initialProject && (!hasLocalizedText(initialProject.title) || !hasLocalizedText(initialProject.project_details?.objective)))}
                onClick={submit}
                className="rounded-full bg-[#E3B24C] hover:bg-[#d4a23e] text-[#2A2521] font-semibold px-6"
              >
                {submitting ? <><Loader2 size={16} className="animate-spin motion-reduce:animate-none" aria-hidden="true" /> {lang === "it" ? "Creazione in corso…" : "Creating…"}</> : t("onboarding.finish")}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <Label className="font-sans text-sm text-[#4A443D] mb-1.5 block">{label}</Label>
    {children}
  </div>
);



